// src/services/brand/clientBrand.ts
// App entry point for a client's brand colors and logo. Every reader in src/
// goes through getClientBrand / getClientBrands; the precedence rules live in
// the shared pure resolver (supabase/functions/_shared/clientBrand.ts), which
// nexus-mcp uses too, so the app and the connector cannot disagree.
//
// Reads are RLS-scoped: brand_profiles and brand_assets are readable by Nexus
// staff and by members of the client. Anon (the public story collector) cannot
// read them and must not use this.

import { supabase } from "@/lib/supabaseClient";

import {
  resolveClientBrand,
  type ClientBrand,
} from "../../../supabase/functions/_shared/clientBrand";

export * from "../../../supabase/functions/_shared/clientBrand";

type ProfileRow = {
  client_id: string;
  color_palette: unknown;
  primary_colors: unknown;
  logo_url: string | null;
};
type AssetRow = { client_id: string; url: string | null; metadata: unknown };
type ClientRow = { id: string; brand: unknown };

/** Fetches brand rows for the given clients and resolves each one. */
export async function getClientBrands(
  clientIds: string[],
): Promise<Map<string, ClientBrand>> {
  const ids = [...new Set(clientIds.filter(Boolean))];
  const out = new Map<string, ClientBrand>();
  if (ids.length === 0) return out;

  const [profiles, assets, clients] = await Promise.all([
    supabase
      .from("brand_profiles")
      .select("client_id, color_palette, primary_colors, logo_url, updated_at")
      .in("client_id", ids)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false }),
    supabase
      .from("brand_assets")
      .select("client_id, url, metadata, created_at")
      .in("client_id", ids)
      .eq("asset_type", "logo")
      .is("deleted_at", null)
      .order("created_at", { ascending: true }),
    supabase.from("clients").select("id, brand").in("id", ids),
  ]);
  for (const r of [profiles, assets, clients]) {
    if (r.error) throw new Error(`Brand lookup failed: ${r.error.message}`);
  }

  // Newest profile per client wins (ordered by updated_at desc above).
  const profileBy = new Map<string, ProfileRow>();
  for (const p of (profiles.data ?? []) as ProfileRow[]) {
    if (!profileBy.has(p.client_id)) profileBy.set(p.client_id, p);
  }
  const assetsBy = new Map<string, AssetRow[]>();
  for (const a of (assets.data ?? []) as AssetRow[]) {
    assetsBy.set(a.client_id, [...(assetsBy.get(a.client_id) ?? []), a]);
  }
  const brandBy = new Map<string, unknown>();
  for (const c of (clients.data ?? []) as ClientRow[])
    brandBy.set(c.id, c.brand);

  for (const id of ids) {
    out.set(
      id,
      resolveClientBrand({
        profile: profileBy.get(id) ?? null,
        logoAssets: assetsBy.get(id) ?? [],
        clientBrand: brandBy.get(id) ?? null,
      }),
    );
  }
  return out;
}

/** One client's resolved brand: { palette, leadColor, logoUrl, logos }. */
export async function getClientBrand(clientId: string): Promise<ClientBrand> {
  const brands = await getClientBrands([clientId]);
  return (
    brands.get(clientId) ??
    resolveClientBrand({ profile: null, logoAssets: [], clientBrand: null })
  );
}

function swatches(list: { name?: string; hex: string }[]): string {
  return list.map((c) => (c.name ? `${c.name} ${c.hex}` : c.hex)).join(", ");
}

/**
 * Prompt block carrying the client's real hex values and logo URLs, so
 * generated assets use them instead of inventing colors. Returns null when
 * there is neither a palette nor a logo on file (nothing is guessed).
 */
export function buildBrandIdentityBlock(brand: ClientBrand): string | null {
  if (!brand.palette && !brand.logoUrl) return null;
  const lines = ["## CLIENT VISUAL IDENTITY"];
  const p = brand.palette;
  if (p) {
    lines.push(
      "Use these exact hex values. Do not invent or approximate brand colors.",
      `Lead color: ${brand.leadColor}`,
      `Primary: ${swatches(p.primary)}`,
    );
    if (p.secondary.length) lines.push(`Secondary: ${swatches(p.secondary)}`);
    for (const [label, list] of Object.entries(p.subPalettes)) {
      lines.push(`${label}: ${swatches(list)}`);
    }
    if (p.notes) lines.push(`Notes: ${p.notes}`);
  }
  if (brand.logoUrl) lines.push(`Logo: ${brand.logoUrl}`);
  for (const l of brand.logos) {
    if (l.url !== brand.logoUrl) lines.push(`Logo (${l.variant}): ${l.url}`);
  }
  return lines.join("\n");
}
